var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Matrix23 } from '@seddi/math-v2';
import { Vector2 } from '@seddi/three';
import { filterMeshes } from '@seddi/ui-three-viewer';
import { getInverseTransformMatrix } from '@/core/transformRt2';
import { BufferAttributeAccessor2 } from '@/viewer/bufferAttributeAccessor';
import { computeWorldPosition } from '@/viewer/mesh';
import { build as buildBvh, dispose as disposeBvh } from '@/viewer/meshBvh';
const compareIntersect = (a, b) => a.distance - b.distance;
const disposeMesh = (mesh) => {
    if (mesh.bvh) {
        disposeBvh(mesh.bvh);
    }
};
const disposeMeshes = (meshes) => {
    for (const mesh of meshes) {
        disposeMesh(mesh);
    }
    meshes.length = 0;
};
const intersect = (collider, ray, intersects = []) => {
    const UNDEFINED_IDX = 4294967295;
    const result = collider.bvh.pIntersector.intersect(ray.origin, ray.direction, 1e-2, 1e2);
    if (result.triangle !== UNDEFINED_IDX) {
        intersects.push({
            distance: result.distance + collider.polygonOffset,
            meshId: collider.id,
            triangle: result.triangle,
            u: result.u,
            v: result.v,
        });
        return true;
    }
    return false;
};
const isPointInsideLogo = (() => {
    const half = new Vector2();
    const lm = new Matrix23();
    const lp = new Vector2();
    return (size, transform, p) => {
        half.set(size.x * 0.5, size.y * 0.5);
        getInverseTransformMatrix(transform, lm);
        lp.set(p.x, p.y);
        lm.transformRef(lp);
        if (Math.abs(lp.x) > half.x || Math.abs(lp.y) > half.y) {
            return false;
        }
        return true;
    };
})();
export class Raycaster {
    constructor() {
        this.intersectLogos = (() => {
            const intersects = [];
            const uv0 = new Vector2();
            const uv1 = new Vector2();
            const uv2 = new Vector2();
            return (ray) => {
                const { colliders, hit } = this;
                if (!colliders.length) {
                    hit.logoId = undefined;
                    hit.meshId = undefined;
                    return hit;
                }
                intersects.length = 0;
                for (const collider of colliders) {
                    intersect(collider, ray, intersects);
                }
                intersects.sort(compareIntersect);
                if (!intersects.length) {
                    hit.logoId = undefined;
                    hit.meshId = undefined;
                    return hit;
                }
                const is = intersects[0];
                hit.logoId = undefined;
                hit.meshId = is.meshId;
                const collider = colliders.find(c => c.id === is.meshId);
                const { index, uv } = collider.accessor;
                if (uv) {
                    const i0 = index[is.triangle * 3];
                    const i1 = index[is.triangle * 3 + 1];
                    const i2 = index[is.triangle * 3 + 2];
                    uv.at(i0, uv0).multiplyScalar(1.0 - is.u - is.v);
                    uv.at(i1, uv1).multiplyScalar(is.u);
                    uv.at(i2, uv2).multiplyScalar(is.v);
                    uv0.add(uv1).add(uv2);
                    hit.uv.copy(uv0);
                    for (const logo of collider.logos) {
                        if (isPointInsideLogo(logo.size, logo.transform, uv0)) {
                            hit.logoId = logo.id;
                            return hit;
                        }
                    }
                }
                return hit;
            };
        })();
        this.colliders = [];
        this.hit = {
            logoId: undefined,
            meshId: undefined,
            uv: new Vector2(),
        };
        this.logos = [];
        this.regions = [];
        this.models = new Map();
    }
    dispose() {
        this.colliders.length = 0;
        this.logos = [];
        this.regions = [];
        if (this.garmentGeometry) {
            disposeMeshes(this.garmentGeometry);
            this.garmentGeometry = undefined;
        }
        for (const [, meshes] of this.models) {
            disposeMeshes(meshes);
        }
        this.models.clear();
    }
    buildGarmentGeometry(garmentGeometry) {
        return __awaiter(this, void 0, void 0, function* () {
            const accel = yield Promise.all(Array.from(garmentGeometry.pieces, (_a) => __awaiter(this, [_a], void 0, function* ([id, piece]) {
                const index = piece.getIndex().array;
                const position = piece.getAttribute('position').array;
                const uv = piece.getAttribute('uv');
                const bvh = yield buildBvh(index, position);
                const accessor = {
                    index,
                    uv: {
                        at: (i, ref) => {
                            ref.x = uv.getX(i);
                            ref.y = uv.getY(i);
                            return ref;
                        },
                    },
                };
                return { accessor, bvh, id, polygonOffset: 0 };
            })));
            this.garmentGeometry = accel;
            this.initCollidersFromGarmentGeometry();
            this.updateLogosInternal();
        });
    }
    buildModel(model) {
        return __awaiter(this, void 0, void 0, function* () {
            const modelMeshes = filterMeshes(model);
            const accel = yield Promise.all(modelMeshes.map((mesh) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c;
                const geometry = mesh.geometry;
                const index = geometry.index.array;
                const position = computeWorldPosition(mesh);
                const bvh = yield buildBvh(index, position);
                const uv = geometry.getAttribute('uv');
                const accessor = {
                    index,
                    uv: uv ? new BufferAttributeAccessor2(uv) : undefined,
                };
                const polygonOffset = ((_a = mesh.userData) === null || _a === void 0 ? void 0 : _a.isMeshDecal) ? -1e-5 : 0;
                const name = ((_c = (_b = mesh.userData) === null || _b === void 0 ? void 0 : _b.name) !== null && _c !== void 0 ? _c : mesh.name);
                return { accessor, bvh, id: name, polygonOffset };
            })));
            this.models.set(model, accel);
            this.initCollidersFromModel(accel);
            this.updateLogosInternal();
        });
    }
    updateLogos(logos) {
        this.logos = logos;
        this.updateLogosInternal();
    }
    updateRegions(regions) {
        this.colliders.length = 0;
        this.regions = regions;
        this.initCollidersFromGarmentGeometry();
        this.initMeshesFromModels();
        this.updateLogosInternal();
    }
    initCollidersFromGarmentGeometry() {
        var _a, _b;
        const { colliders, garmentGeometry: meshes, regions } = this;
        if (!meshes)
            return;
        const pieceToRegions = {};
        for (const region of regions) {
            const entityId = region.entityId;
            (_a = pieceToRegions[entityId]) !== null && _a !== void 0 ? _a : (pieceToRegions[entityId] = []);
            pieceToRegions[entityId].push(region.id);
        }
        for (const mesh of meshes) {
            const { accessor, bvh, id, polygonOffset } = mesh;
            const regionIds = (_b = pieceToRegions[id]) !== null && _b !== void 0 ? _b : [];
            colliders.push({
                accessor,
                bvh,
                id: crypto.randomUUID(),
                regions: regionIds,
                logos: [],
                polygonOffset,
            });
        }
    }
    initCollidersFromModel(meshes) {
        var _a, _b;
        const { colliders, regions } = this;
        const meshToRegions = {};
        for (const region of regions) {
            const meshId = region.pieceName;
            (_a = meshToRegions[meshId]) !== null && _a !== void 0 ? _a : (meshToRegions[meshId] = []);
            meshToRegions[meshId].push(region.id);
        }
        for (const mesh of meshes) {
            const { accessor, bvh, id, polygonOffset } = mesh;
            const regionIds = (_b = meshToRegions[id]) !== null && _b !== void 0 ? _b : [];
            colliders.push({
                accessor,
                bvh,
                id: crypto.randomUUID(),
                regions: regionIds,
                logos: [],
                polygonOffset,
            });
        }
    }
    initMeshesFromModels() {
        for (const [, mesh] of this.models) {
            this.initCollidersFromModel(mesh);
        }
    }
    updateLogosInternal() {
        const { colliders, logos } = this;
        for (const collider of colliders) {
            const { regions } = collider;
            collider.logos = logos.filter(l => regions.includes(l.region));
        }
    }
}
